import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage, Input, PasswordInput } from "../forms";
import { AUTH_FIELD_ITEM_CLASS, AUTH_FLOATING_LABEL_CLASS, AUTH_INPUT_CLASS } from "./authClasses";

import { cn } from "@/utils";
import type { InputHTMLAttributes } from "react";

export type TAuthTextFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  required?: boolean;
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, "name" | "defaultValue">;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
};

export function AuthTextField<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  required,
  inputProps,
  className,
  inputClassName,
  labelClassName,
}: TAuthTextFieldProps<TFieldValues>) {
  const id = String(name);

  // Pull className out of inputProps so it can be merged into cn() below
  // instead of spreading over and silently wiping AUTH_INPUT_CLASS.
  const { className: inputPropsClassName, ...restInputProps } = inputProps ?? {};

  const isPassword = restInputProps?.type === "password";
  const isRequired = required ?? restInputProps?.required;

  // iOS Safari ignores padding-top on type="email", breaking the floating label.
  // Normalise to type="text" with inputMode/autoComplete so the keyboard and
  // autofill still work while CSS padding is respected across all browsers.
  const resolvedInputProps =
    restInputProps?.type === "email"
      ? { inputMode: "email" as const, autoComplete: "email", ...restInputProps, type: "text" as const }
      : restInputProps;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={cn(AUTH_FIELD_ITEM_CLASS, className)}>
          <FormLabel
            htmlFor={id}
            className={cn(AUTH_FLOATING_LABEL_CLASS, labelClassName)}
          >
            {label}
            {isRequired && (
              <span aria-hidden="true" className="ml-0.5 text-destructive">
                *
              </span>
            )}
          </FormLabel>
          <FormControl>
            {isPassword ? (
              <PasswordInput
                id={id}
                className={cn(AUTH_INPUT_CLASS, inputClassName, inputPropsClassName)}
                aria-required={isRequired ? "true" : undefined}
                {...resolvedInputProps}
                {...field}
              />
            ) : (
              <Input
                id={id}
                className={cn(AUTH_INPUT_CLASS, inputClassName, inputPropsClassName)}
                aria-required={isRequired ? "true" : undefined}
                {...resolvedInputProps}
                {...field}
              />
            )}
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
